import { useEffect, useState } from 'react';
import { useSubscription } from '@/store/subscription';
import { useWorkspace } from '@/store/workspace';
import { supabase } from '@/lib/supabase';

interface FeatureGate {
  // Feature availability
  canUseAI: boolean;
  canCreateProject: boolean;

  // Tier status
  isPro: boolean;
  isFree: boolean;
  isTrialing: boolean;

  // Limits
  projectLimit: number;
  currentProjectCount: number;
  projectsRemaining: number;

  // Trial info
  trialDaysRemaining: number;

  // Loading state
  isLoading: boolean;
}

/**
 * Hook for feature gating based on subscription tier
 * Use this to check if a user can access certain features
 */
export function useFeatureGate(): FeatureGate {
  const {
    isLoading: subscriptionLoading,
    isPro,
    isFree,
    isTrialing,
    canUseAI,
    projectLimit,
    trialDaysRemaining,
  } = useSubscription();

  const { currentWorkspace } = useWorkspace();
  const [currentProjectCount, setCurrentProjectCount] = useState(0);
  const [isLoadingProjects, setIsLoadingProjects] = useState(true);

  // Fetch current project count for the workspace
  useEffect(() => {
    const fetchProjectCount = async () => {
      if (!currentWorkspace) {
        setCurrentProjectCount(0);
        setIsLoadingProjects(false);
        return;
      }

      try {
        // Drafts don't count toward the project limit — only promoted
        // projects do. Promotion (wizard "Create Project" / ProjectDetails
        // "Mark as Ready") re-checks `canCreateProject` at the call site.
        const { count, error } = await supabase
          .from('projects')
          .select('*', { count: 'exact', head: true })
          .eq('workspace_id', currentWorkspace.id)
          .neq('status', 'draft');

        if (error) {
          console.error('Error fetching project count:', error);
          setCurrentProjectCount(0);
        } else {
          setCurrentProjectCount(count || 0);
        }
      } catch (error) {
        console.error('Error fetching project count:', error);
        setCurrentProjectCount(0);
      } finally {
        setIsLoadingProjects(false);
      }
    };

    fetchProjectCount();
  }, [currentWorkspace]);

  const projectsRemaining = Math.max(0, projectLimit - currentProjectCount);
  const canCreateProject = isPro || currentProjectCount < projectLimit;

  return {
    canUseAI,
    canCreateProject,
    isPro,
    isFree,
    isTrialing,
    projectLimit,
    currentProjectCount,
    projectsRemaining,
    trialDaysRemaining,
    isLoading: subscriptionLoading || isLoadingProjects,
  };
}

/**
 * Simple check functions for use outside of React components
 * These use the store directly
 */
export const featureGate = {
  canUseAI: () => {
    const { useSubscriptionStore } = require('@/store/subscription');
    return useSubscriptionStore.getState().canUseAI();
  },

  isPro: () => {
    const { useSubscriptionStore } = require('@/store/subscription');
    return useSubscriptionStore.getState().isPro();
  },

  isFree: () => {
    const { useSubscriptionStore } = require('@/store/subscription');
    return useSubscriptionStore.getState().isFree();
  },
};
